import type { Index } from "flexsearch";
import FlexSearch from "flexsearch";
import invariant from "tiny-invariant";
import { z } from "zod";
import type { ArmorType, Slot, WeaponType } from "~/types";
import type { GameVer, Lang, lstring } from "~/util/Lang";

export type Int = number;
type Float = number;

export type GameData = {
  all_gd?: AllGameData;
  gver: GameVer;
  items: Map<string, Item>;
  itemsById: Map<number, Item>; // 🔥 Новое поле
  races: Map<string, Race>;
  npcs: Map<string, Npc>;
  quests: Map<number, Quest>;
  skills: Map<number, Skill>;
  profs: Map<string, Prof>;
  locs: Map<string, Loc>;
  sets: Map<number, Set>;
  sa: Map<string, SpecialAbility>;
  doSearch: (q: string) => Promise<Entity[]>; // 🔥 Новое поле
  doItemSearch: (q: string) => Promise<Entity[]>; // 🔥 Новое поле
};

export type RawGameData = {
  all_gd?: AllGameData;
  gver: GameVer;
  items: Map<string, Item>;
  races: Map<string, Race>;
  npcs: Map<string, Npc>;
  quests: Map<number, Quest>;
  skills: Map<number, Skill>;
  profs: Map<string, Prof>;
  locs: Map<string, Loc>;
  sets: Map<number, Set>;
  sa: Map<string, SpecialAbility>;
};

export type AllGameData = Record<GameVer, GameData>;
export const AllGameData = z.custom<AllGameData>();

export function createGameData(raw: RawGameData) {
  const entities: Entity[] = [];
  const search: Index<number> = new (FlexSearch as any).Index({
    language: "en",
    tokenize: "forward",
    optimize: true,
    resolution: 9,
    minlength: 2,
    fastupdate: false,
    context: false,
  });
  const itemSearch: Index<number> = new (FlexSearch as any).Index({
    language: "en",
    tokenize: "forward",
    optimize: true,
    resolution: 9,
    minlength: 2,
    fastupdate: false,
    context: false,
  });
  function addSearch(entity: Entity) {
    const id = entities.length;
    entities.push(entity);
    search.add(id, entity.name);
  }
  function addItemSearch(entity: Entity) {
    const id = entities.length;
    entities.push(entity);
    search.add(id, entity.name);
  }
  async function doSearch(q: string) {
    const ids = await search.search(q, { limit: 14 });
    const results = ids.map((i) => entities[i]);
    return results;
  }
  async function doItemSearch(q: string) {
    const ids = await search.search(q, { limit: 6 });
    const results = ids.map((i) => entities[i]);
    return results;
  }

  for (const item of raw.items.values()) {
    addSearch({
      id: item.itemName,
      name: `${item.name.en ?? item.itemName} ${
        item.addName.en ? `[${item.addName.en}]` : ``
      }`.trim(),
      icon: item.icon,
      type: "item",
      level: null,
    });
    // addItemSearch({
    //   id: item.itemName,
    //   name: `${item.name.en ?? item.itemName} ${
    //     item.addName.en ? `[${item.addName.en}]` : ``
    //   }`.trim(),
    //   icon: item.icon,
    //   type: "item",
    // });
  }
  for (const npc of raw.npcs.values()) {
    addSearch({
      id: npc.npcName,
      icon: null,
      name: npc.name.en,
      type: "npc",
      level: npc.level,
    });
  }
  for (const quest of raw.quests.values()) {
    addSearch({
      name: quest.name.en,
      icon: null,
      type: "quest",
      id: quest.id.toString(),
      level: null,
    });
  }
  for (const skill of raw.skills.values()) {
    addSearch({
      name: skill.name.en,
      icon: skill.icon,
      type: "skill",
      id: skill.cardName + ":" + 1,
      level: null,
    });
  }

  const itemsById = new Map<number, Item>();
  for (const item of raw.items.values()) {
    itemsById.set(item.id, item);
  }

  return {
    all_gd: undefined,
    gver: raw.gver,
    items: raw.items,
    itemsById,
    races: raw.races,
    npcs: raw.npcs,
    quests: raw.quests,
    skills: raw.skills,
    profs: raw.profs,
    locs: raw.locs,
    sets: raw.sets,
    sa: raw.sa,
    doSearch,
    doItemSearch,
  };
}

export function addNpcSpawn(params: { npc: Npc; pos: Point[] }) {
  const spawn: NpcSpawn = {
    npc: params.npc,
    pos: params.pos,
  };
  params.npc.spawns.push(spawn);
}
export function addNpcDrop(params: {
  chance: number;
  countMin: number;
  countMax: number;
  npc: Npc;
  item: Item;
}) {
  const npc = params.npc;
  invariant(npc);
  const item = params.item;
  invariant(item);
  const drop: NpcDrop = {
    chance: params.chance,
    countMin: params.countMin,
    countMax: params.countMax,
    npc,
    item,
  };
  npc.dropList.push(drop);
  item.dropList.push(drop);
}
export function addNpcHerbs(params: {
  chance: number;
  countMin: number;
  countMax: number;
  npc: Npc;
  item: Item;
}) {
  const npc = params.npc;
  invariant(npc);
  const item = params.item;
  invariant(item);
  const drop: NpcDrop = {
    chance: params.chance,
    countMin: params.countMin,
    countMax: params.countMax,
    npc,
    item,
  };
  npc.herbList.push(drop);
  item.herbList.push(drop);
}
export function addNpcBuffs(params: {
  minLevel: number;
  maxLevel: number;
  group: "fighter" | "mage";
  npc: Npc;
  skill: SkillLevel;
}) {
  const npc = params.npc;
  invariant(npc);
  const skill = params.skill;
  invariant(skill);
  const buff: NpcBuff = {
    group: params.group,
    maxLevel: params.maxLevel,
    minLevel: params.minLevel,
    skill: skill,
    npc: npc,
  };
  npc.buffList.push(buff);
  // skill.buffList.push(buff);
}
export function addNpcSpoil(params: {
  chance: number;
  countMin: number;
  countMax: number;
  npc: Npc;
  item: Item;
}) {
  const npc = params.npc;
  invariant(npc);
  const item = params.item;
  invariant(item);
  const drop: NpcDrop = {
    chance: params.chance,
    countMin: params.countMin,
    countMax: params.countMax,
    npc,
    item,
  };
  npc.spoilList.push(drop);
  item.spoilList.push(drop);
}
export function addNpcSkill(params: { skill: SkillLevel; npc: Npc }) {
  const npc = params.npc;
  invariant(npc);
  const skill = params.skill;
  invariant(skill);
  npc.skills.push(skill);
  // skill.npcs.push(npc);
}

export type Effect = {
  effectName: string;
  app: string[];
  value: number;
  per: boolean;
};

export type SkillLevel = {
  id: Int;
  skillName: string;
  cardName: string;
  name: lstring;
  desc: lstring;
  level: Int | null;
  icon: string;
  operateType: "P" | "A1" | "A2" | "A3" | "T" | "DA1" | "DA2" | "CA1" | "CA5";
  effect: string;
  castRange: number;
  hp_consume: number;
  mp_consume1: number;
  mp_consume2: number;
  effects: Effect[];
};

export type SkillOperateType =
  | "P"
  | "A1"
  | "A2"
  | "A3"
  | "T"
  | "DA1"
  | "DA2"
  | "CA1"
  | "CA5";

export type SkillEnchant = {
  level: number;
  type: "power" | "cost" | "chance" | "recovery" | "time";
  chance: number[][];
  skill: SkillLevel;
};

export type Skill = {
  id: Int;
  cardName: string;
  name: lstring;
  icon: string;
  operateType: SkillOperateType;
  levels: Map<number, SkillLevel>;
  enchanting: Map<string, SkillEnchant[]>;
  npcs: Npc[];
  profs: { prof: Prof; study: { level: number; skills: SkillLevel[] }[] }[];
  buffList: NpcBuff[];
};

export type CapsuleContaints = {
  items: { item: Item; count: number }[];
  chance: number;
};

export type CapsuleDrop = {
  item: Item;
  count: number;
  chance: number;
};

export type Item = {
  id: Int;
  itemName: string;
  type: string;
  icon: string;
  name: lstring;
  addName: lstring;
  desc: lstring;
  weaponType: WeaponType;
  armorType: ArmorType;
  etcitemType: string | null;
  kind?: "part" | "crystal" | "gemstone" | "resource";
  slotBitType: Slot;
  weight: Int | null;
  consumeType: string | null;
  initialCount: Int | null;
  maximumCount: Int | null;
  soulshotCount: Int | null;
  spiritshotCount: Int | null;
  immediateEffect: boolean;
  defaultPrice: Int | null;
  itemSkill: string | null;
  criticalAttackSkill: string | null;
  materialType: string | null;
  crystalType: string | null;
  crystalCount: Int | null;
  isTrade: boolean;
  isDrop: boolean;
  isDestruct: boolean;

  physicalDamage: Int | null;
  randomDamage: Int | null;
  canPenetrate: boolean;
  critical: Int | null;
  hitModify: Float | null;
  avoidModify: Int | null;
  dualFhitRate: Int | null;
  shieldDefense: Int | null;
  shieldDefenseRate: Int | null;
  attackRange: Int | null;
  effectiveRange: Int | null;
  damageRange: string | null;
  attackSpeed: Int | null;
  reuseDelay: Int | null;
  mpConsume: Int | null;
  magicalDamage: Int | null;
  durability: Int | null;
  physicalDefense: Int | null;
  magicalDefense: Int | null;
  mpBonus: Int | null;
  magicWeapon: boolean;

  dropList: NpcDrop[];
  spoilList: NpcDrop[];
  herbList: NpcDrop[];
  sellList: Merchant[];
  recipe: Recipe[];
  product: Product[];
  material: Material[];
  inRecipe: Item[];
  inQuests: Quest[];
  questReward: Quest[];
  exchange_from: ItemMultisell[];
  exchange_for: ItemMultisell[];
  specialAbility: Item[];
  sets: Set[];
  enchantBonus: EnchantBonus[];
  contains: CapsuleContaints[];
  containsIn: CapsuleDrop[];
};

export type EnchantBonus = {
  level: Int;
  pAtk: Int;
  mAtk: Int;
  pDef: Int;
  mDef: Int;
  crystals: Int[];
  chance: Int;
};

export type Set = {
  id: Int;
  setName: string;
  icon: string;
  name: lstring;
  desc: lstring;
  items: {
    slotHead: Item[];
    slotChest: Item[];
    slotLegs: Item[];
    slotGloves: Item[];
    slotFeet: Item[];
    slotLhand: Item[];
  };
};

export type SA = {
  item: Item;
  abilityList: Item[];
};

export type Race = {
  raceName: string;
  icon: string;
  name: lstring;
  player?: boolean;
};

export type Npc = {
  id: Int;
  npcName: string;
  name: lstring;
  nick: lstring;
  nickColor: "default" | "quest" | "raid";
  type: string;
  ai: string;
  classes: string[];
  race: Race;
  level: Int | null;
  exp: Float | null;
  sp: Float | null;
  orgHp: Float | null;
  orgMp: Float | null;
  orgHpRegen: Float | null;
  orgMpRegen: Float | null;
  agro: boolean | null;
  agroRange: Int | null;
  baseDefend: Float | null;
  baseMagicAttack: Float | null;
  baseMagicDefend: Float | null;
  basePhysicalAttack: Float | null;
  physicalHitModify: Float | null;
  physicalAvoidModify: Float | null;
  baseAttackSpeed: Int | null;
  baseReuseDelay: Int | null;
  baseCritical: Int | null;
  pAtk: Int | null;
  pDef: Int | null;
  mAtk: Int | null;
  mDef: Int | null;
  pSpd: Int | null;
  mSpd: Int | null;
  pCritical: Int | null;
  accuracy: Int | null;
  evasion: Int | null;
  baseMovingSpeed: number[];
  con: Int | null;
  dex: Int | null;
  int: Int | null;
  men: Int | null;
  str: Int | null;
  wit: Int | null;
  magicUseSpeedModify: Float | null;
  merchant: Merchant | null;
  spawns: NpcSpawn[];
  locs: Loc[];
  skills: SkillLevel[];
  dropList: NpcDrop[];
  spoilList: NpcDrop[];
  herbList: NpcDrop[];
  buffList: NpcBuff[];
  startsQuests: Quest[];
  inQuests: Quest[];
  multisell: Multisell[];
  screenshots: Screenshot[];
  teleports: Teleport[][];
  canUseSA: Int | null;
  soulshotCount: Int;
  spiritshotCount: Int;
  summonLevels: Summon[];
  model?: {
    meshPath: string;
    meshName: string;
    texturePath?: string;
    material?: {
      name?: string;
      diffuse: string;
      specular?: string;
      opacity?: string;
      params?: { outputBlending: number };
    }[];
    animationPath?: string;
    animation?: string;
  };
};

type Summon = {
  npcName: string;
  level: Int;
  pAtk: Int | null;
  name: lstring;
  race: Race;
  orgHp: Float | null;
  orgMp: Float | null;
  pDef: Int | null;
  mAtk: Int | null;
  mDef: Int | null;
  pSpd: Int | null;
  mSpd: Int | null;
  pCritical: Int | null;
  accuracy: Int | null;
  evasion: Int | null;
  baseMovingSpeed: number[];
};

type MultisellItem = {
  item: Item;
  count: Int;
};

export type Multisell = {
  resultItems: MultisellItem[];
  requiredItems: MultisellItem[];
  npc: Npc;
};

export type ItemMultisell = {
  resultItems: MultisellItem[];
  requiredItems: MultisellItem[];
  npcs: Npc[];
};

export type Teleport = {
  name: lstring;
  x: number;
  y: number;
  price: number;
};

export type Screenshot = {
  filename: string;
  height: number;
  width: number;
};

export type Product = {
  recipe: Recipe;
  item: Item;
  count: Int;
};

export type Material = {
  recipe: Recipe;
  item: Item;
  count: Int;
};

export type Recipe = {
  id: Int;
  recipeName: string;
  item: Item;
  productList: Product[];
  materialList: Material[];
  successRate: Int | null;
  level: Int | null;
  mpConsume: Int | null;
};

export type NpcSpawn = {
  npc: Npc;
  pos: Point[];
};

export type NpcDrop = {
  npc: Npc;
  item: Item;
  countMin: Int;
  countMax: Int;
  chance: Float;
};

export type NpcBuff = {
  npc: Npc;
  skill: SkillLevel;
  minLevel: Int;
  maxLevel: Int;
  group: "mage" | "fighter";
};

export type SellList = {
  name: string;
  items: SellItem[];
};

export type Merchant = {
  npc: Npc;
  tax: number;
  sellLists: SellList[];
};

export type SellItem = {
  item: Item;
  price: null | [Item, number][];
};

export type Quest = {
  id: Int;
  uname: string;
  name: lstring;
  desc: lstring;
  minLevel: number | null;
  maxLevel: number | null;
  progs: Record<Lang, QuestProg[]>;
  walkthrough: Record<Lang, QuestWalkthroughStep[]>;
  startNpcs: Npc[];
  type: string;
  requirements: QuestReq;
  reward: QuestReward;
  npcs: Npc[];
  items: Item[];
};

export type QuestWalkthroughStep = {
  pieces: QuestWalkthroughPiece[];
};

export type QuestWalkthroughPiece =
  | string
  | { t: "by"; name: string }
  | {
      t: "item";
      id: string;
      name: string;
      icon: string;
      count: number | null;
    }
  | {
      t: "npc";
      id: string;
      name: string;
      title: string;
      type: string;
      nickColor: "default" | "raid" | "quest";
    }
  | {
      t: "quest";
      id: number;
      uname: string;
      name: string;
    }
  | {
      t: "exp";
      exp: number;
    }
  | {
      t: "sp";
      sp: number;
    };

export type QuestReq = {
  races: Race[];
  occupation: Prof[];
  category: string;
  items: [Item, Int][] | [];
};

export type QuestProg = {
  progId: Int;
  name: string;
  desc: string;
  items: { item: Item; count: Int }[];
};

export type QuestReward = {
  exp?: number;
  sp?: number;
  items: Item[];
};

export type Prof = {
  profName: string;
  name: lstring;
  quests: Quest[];
  skills: ProfSkill[];
  prev: Prof | null;
  next: Prof[];
  rank?: number;
  trainingLevels?: number[];
};

export type ProfSkill = {
  cardName: string;
  skill: SkillLevel;
  level: number;
  sp: number;
  autoGet: boolean;
  books: [Item, number][];
};

export type XY = {
  x: number;
  y: number;
};

// export type Loc = {
//   id: number;
//   uname: string;
//   name: lstring;
//   desc?: { en?: string; ru?: string; };
//   area: XY[];
//   seaLevel: number[];
//   npcs: Npc[];
//   dropList: LocDropList[];
//   spoilList: LocSpoilList[];
//   startsQuests: Quest[];
//   sellList: LocSellList[];
//   screenshots: Screenshot[];
//   levels: { min: number | null; max: number | null; };
// };

export type Loc = {
  territory: {
    areaName: string;
    name: lstring;
  };
  locName: string;
  name: lstring;
  type?: "town" | "harbor" | "castle";
  desc?: { en?: string; ru?: string };
  area: XY[];
  zLevel: number[];
  npcs: Npc[];
  levels: { min: number | null; max: number | null };
  dropList: LocDropList[];
  spoilList: LocSpoilList[];
  startsQuests: Quest[];
  sellList: LocSellList[];
  screenshots: Screenshot[];
};

export type LocSellList = {
  item: Item;
  price: Number;
  npcs: Npc[];
};
export type LocDropList = {
  item: Item;
  npcs: Npc[];
};
export type LocSpoilList = {
  item: Item;
  npcs: Npc[];
};

export type SpecialAbility = {
  item: Item;
  level: number;
  abilities: { saRed: Item; saGreen: Item; saBlue: Item };
};

export const Point = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number(),
});
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Point = z.infer<typeof Point>;

//
// Other utils
//

export type Entity = {
  id: string;
  type: string;
  name: string;
  icon: string | null;
  level: number | null;
};

type Scalar = number | string | boolean;
type Selector<T> = FunSelector<T, any> | ShapeSelector<T>;
type ShapeSelector<T> = T extends Scalar
  ? true
  : T extends ReadonlyArray<infer U>
  ? ArraySelector<U>
  : T extends {}
  ? {
      [K in keyof T]?: Selector<T[K]>;
    }
  : never;
type ArraySelector<T> = { select: Selector<T> };
type FunSelector<T, R> = (x: T) => R;

type Selected<T, S extends Selector<T>> = S extends FunSelector<T, infer R>
  ? R
  : T extends Scalar
  ? S extends true
    ? T
    : never
  : T extends ReadonlyArray<infer U>
  ? S extends ArraySelector<U>
    ? Array<Selected<U, S["select"]>>
    : never
  : T extends {}
  ? {
      [K in keyof S]: K extends keyof T
        ? S[K] extends Selector<T[K]>
          ? Selected<T[K], S[K]>
          : never
        : never;
    }
  : T extends null
  ? null
  : T extends undefined
  ? undefined
  : "select_unhandled";

export function select<T, S extends Selector<T>>(
  obj: T,
  selector: S
): Selected<T, S> {
  if (typeof selector === "function") {
    return selector(obj);
  }

  switch (typeof obj) {
    case "boolean":
    case "string":
    case "number":
      if (selector === true) {
        return obj as any;
      } else {
        return null as any;
      }
    case "object": {
      if (obj === null) {
        return null as any;
      }
      if (Array.isArray(obj)) {
        return obj.map((el) => select(el, (selector as any).select)) as any;
      }
      const out: any = {};
      for (const key of Object.keys(selector as object)) {
        out[key] = select((obj as any)[key], (selector as any)[key]);
      }
      return out;
    }
    case "undefined":
      return undefined as any;
  }
  console.log("Unhandled:", obj);
  throw new Error("select unhandled" + JSON.stringify(obj));
}
